import { ChangeDetectionStrategy, Component, OnInit, inject, ViewChild, AfterViewInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AdditionalInfoComponent } from '@optima-stride-ai/optima-app-framework';
import { FormLayoutComponent, IconSelectorComponent, IFormLayout, IFormLayoutContent, IFormLayoutFooter, IFormLayoutHeader, IPolymorphicControlMeta, PolymorphicControlService, PolyMorphicControlTypes, ToastService, ProgressIndicatorComponent, ProcessProgressType, ProcessProgressView, ShimmerVariant, iProcessProgress } from '@optima-stride-ai/optima-components';
import { DateRange } from '../../../models/interfaces/date-range-model';
import { IAdditionalInfo, IRelationshipConfiguration, FieldTypes, IBizEntityRelationships, RelationshipLinkType, RelationshipTargetType, IBizEntityRelationTarget, IBizEntity, BizEntityTypeCategory, IBizEntityType } from '@optima-stride-ai/core';
import { IObjective } from '../objective.model';
import { initFormStateFunc } from '../../utils/form/function-init-form-state';
import { BizEntityParentHierarchyService } from '../../utils/form/resolve-biz-entity-from-parent-hierarchy';
import { AppStateService } from '../../state/app-state';
import { notifyStrategyEntityCreated } from '../../strategy/strategy-explorer/strategy-explorer-creation';
import { objectivesSyncBeforeSendingToAPI, ObjectiveStateStoreService } from '../../objective/objective-state-store/objective-state-store.service';
import { UtilService } from '../../utils/util.service';
import { DurationConstraintsService } from '../../utils/form/duration-constraints.service';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { FormRelationshipFieldsComponent } from '@optima-stride-ai/optima-app-framework';
import { AppRootStateStoreService, benBizEntitySyncBeforeSendingToAPI, BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { IBenBizEntity } from '../../../models/interfaces/biz-entity.model';
import { AutoCreateEntitiesService } from '../../kpi/auto-create-entites';
import { RelationshipStateStoreService, AppFrameworkContextService, IXappCap } from '@optima-stride-ai/optima-app-framework';
import { BizEntityChildrenStatusStoreService, FormBaseEntityComponent } from '@optima-stride-ai/optima-app-framework';
import { TenantStateStoreService, UserContextService } from '@optima-stride-ai/optima-platform';
import { CardComponent, CardBodyComponent, CardHeaderComponent } from '@coreui/angular';
@Component({
  selector: 'app-form-objective',
  imports: [FormBaseEntityComponent, AdditionalInfoComponent, FormLayoutComponent, ReactiveFormsModule, FormRelationshipFieldsComponent, CardComponent, CardBodyComponent, CardHeaderComponent, ProgressIndicatorComponent],
  templateUrl: './form-objective.component.html',
  styleUrl: './form-objective.component.sass',
})
export class FormObjectiveComponent implements OnInit {
  @ViewChild('baseEntityForm') baseEntityFormComponent!: FormBaseEntityComponent;
  @ViewChild('additionalInfoForm') additionalInfoFormComponent!: AdditionalInfoComponent;
  @ViewChildren('relationshipFields') relationshipFieldsComponents!: QueryList<FormRelationshipFieldsComponent>;
  formLayout: IFormLayout = { 
    header: { title: '' }, 
    content: { fieldItems: [] }, 
    footer: { actions: [] } 
  };
  formFooterLayout: IFormLayout = { 
    header: { title: '' }, 
    content: { fieldItems: [] }, 
    footer: { actions: [] } 
  };
  relationshipConfigurations: IRelationshipConfiguration[] = [];
  relationshipAdditionalInfo: { config: IRelationshipConfiguration, name: string, additionalInfo: IAdditionalInfo[] }[] = [];
  relationshipAdditionalInfoForEdit: { config: IRelationshipConfiguration, bizEntity: IBizEntity }[] = [];
  bizEntities: IBizEntity[] = [];
  extractedTargetBizEntities: IBizEntity[] = [];
  isEditing: boolean = false;
  protected isFormLoading = true;
  private baseFormReady = false;
  private flowDataReady = false;
  protected readonly formLoadingProgress: iProcessProgress = { id: 'form-objective-loading', type: ProcessProgressType.Indeterminate };
  protected readonly ProcessProgressView = ProcessProgressView;
  protected readonly ShimmerVariant = ShimmerVariant;
  private isAPICallInProgress: boolean = false;
  // // Primary objective entity (strongly typed)
  objectiveBizEntity!: IObjective;
  // Duration constraints from parent objective (for create mode)
   //  ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️These are business rules.Should be loaded by* code at runtime and handled as data, not as logic.* ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️
  private parentDurationConstraints: { from: string; to: string; value: string } | null = null;
  private readonly polymorphicControlService = inject(PolymorphicControlService);
  private readonly appStateService = inject(AppStateService);
  private readonly objectiveStateStoreService = inject(ObjectiveStateStoreService);
  private readonly bizEntityParentHierarchyService = inject(BizEntityParentHierarchyService);
  private readonly bizEntityTypesStateStoresService = inject(BizEntityTypeStateStoreService);
  private readonly utilService = inject(UtilService);
  private readonly durationConstraintsService = inject(DurationConstraintsService);
  private readonly toastService = inject(ToastService);
  private readonly autoCreateEntitiesService = inject(AutoCreateEntitiesService);
  private readonly relationshipStateStoreService = inject(RelationshipStateStoreService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly appRootStateStoreService = inject(AppRootStateStoreService);
  private readonly bizEntityChildrenStatusStoreService = inject(BizEntityChildrenStatusStoreService);
  private readonly appFrameworkContextService = inject(AppFrameworkContextService);
  private readonly tenantStateStoreService = inject(TenantStateStoreService);
  private readonly userContextService = inject(UserContextService);
  private currentAppId: string | undefined;

  /** Allow Objective category entities as parent (nested objectives). */
  readonly enableParentForCategory: BizEntityTypeCategory[] = [BizEntityTypeCategory.BenzaitenObjective];

  get formHeaderTitle(): string {
    return this.objectiveBizEntity?.type?.name || 'Objective';
  }

  get formHeaderSubtitle(): string {
    const typeName = this.objectiveBizEntity?.type?.name || 'Objective';
    const entityName = this.objectiveBizEntity?.name || typeName;
    return this.isEditing
      ? `Edit ${entityName} to track strategy`
      : `Create ${typeName} to track strategy`;
  }

  constructor() {
    let state = initFormStateFunc(this.appStateService);
    this.isEditing = state.isEditing;
    this.objectiveBizEntity = state.bizEntity as IObjective;
  }
  ngOnInit(): void {
    if (!this.currentAppId) {
      this.currentAppId = this.appFrameworkContextService.currentAppId;
    }
    if (this.isEditing) {
      // Fetch the objective being edited
      const objectiveId = this.objectiveBizEntity?.id;
      const parentId = this.objectiveBizEntity.parentId;
      
      this.objectiveStateStoreService.getState(this.bizEntityTypesStateStoresService, [objectiveId])
        .then(async (results) => {
          const objective = results?.[0];
          
          if (objective) {
            this.objectiveBizEntity = objective as IObjective;
          }
          
          // If there's a parent, resolve objective from parent hierarchy
          if (parentId) {
            try {
              const parentEntities = await this.bizEntityStateStoreService.getState(this.bizEntityTypesStateStoresService,undefined,[parentId]);
              const parentEntity = parentEntities?.[0];
              
              if (parentEntity) {
                const resolvedObjective = await this.bizEntityParentHierarchyService.resolveAndFetchObjectiveFromParentHierarchy(parentEntity);
                if (resolvedObjective?.duration) {
                  this.parentDurationConstraints = this.bizEntityParentHierarchyService.getDurationDisplayValue(resolvedObjective);
                }
              }
            } catch (error) {
              // Silently handle error - no constraints applied
            }
          }
          
          // Build form with constraints (if any) applied
          this.buildFormLayout();
          this.formFooterLayout = this.buildFormFooterLayout();
          
          // Load relationships if applicable
          if (this.doesBizEntityHaveRelationship()) {
            this.relationshipStateStoreService.getState([this.objectiveBizEntity?.id?.toString()], this.bizEntityTypeStateStoreService, this.bizEntityStateStoreService, this.bizEntityChildrenStatusStoreService, true)
              .then((relationships) => {
                if (relationships) {
                  this.extractedTargetBizEntities = this.extractTargetBizEntities(relationships);
                }
              })
              .catch(() => {
              })
              .finally(() => {
                  this.markFlowDataReady();
              });
          } else {
                this.markFlowDataReady();
          }
        }).catch(() => {
              this.markFlowDataReady();
        });
    } else {
      // In create mode, check if there's a context entity from which create was triggered
      const contextEntity = this.appStateService.selectedBizEntityForEdit;
      
      this.doesBizEntityHaveRelationship();
      
      // If there's a context entity, load parent duration constraints if it is an objective
      if (contextEntity?.id) {
        this.objectiveBizEntity.parentId = contextEntity.id.toString();
        
        // Fetch first encountered objective data from parent hierarchy
        this.bizEntityParentHierarchyService.resolveAndFetchObjectiveFromParentHierarchy(contextEntity)
          .then((objective) => {
            if (objective?.duration) {
              this.parentDurationConstraints = this.bizEntityParentHierarchyService.getDurationDisplayValue(objective);
            }
          })
          .finally(() => {
            // Build form after constraints loaded (or failed)
            this.buildFormLayout();
            this.formFooterLayout = this.buildFormFooterLayout();
            this.markFlowDataReady();
          });
      } else {
        // No context entity - build form immediately
        this.buildFormLayout();
        this.formFooterLayout = this.buildFormFooterLayout();
        this.markFlowDataReady();
      }
    }
  }

 
  private buildFormLayout() {
    const layout: IFormLayout = {
      content: this.buildFormLayoutContent(),
      cssClass: 'ben-form-objective-specific'
    };
    this.formLayout = layout;
  }
  private buildFormLayoutContent(): IFormLayoutContent {
    // This method returns the IPolymorphicControlMeta of duration field, including from and to date fields.
    return {
      fieldItems: this.buildFormFields()
    };
  }
  private buildFormFooterLayout(): IFormLayout {
    const layoutFooterContent: IFormLayoutContent = {
      fieldItems: [],
    };
    const layoutFooter: IFormLayoutFooter = {
      actions: [{
        label: this.isEditing ? `Update ${this.objectiveBizEntity?.name}` : `Create ${this.objectiveBizEntity?.type?.name}`,
        icon: 'cilPlus',
      }]
    };
    const layoutFooterMain: IFormLayout = {
      content: layoutFooterContent,
      footer: layoutFooter,
      cssClass: 'ben-form-objective-footer'
    };
    return layoutFooterMain;
  }

  get isDurationFieldValid(): boolean {
    const dateRangeField = this.formLayout.content?.fieldItems?.find(field => field.id === 'duration');
    
    if (!dateRangeField) {
      return false;
    }

    // Check if field is required
    if (dateRangeField.fieldMeta?.isRequired) {
      // Mark field as touched for validation display
      dateRangeField.touched = true;

      // Check if from date is empty
      if (!dateRangeField.dateRangeValueFromString || dateRangeField.dateRangeValueFromString.trim() === '') {
        return false;
      }

      // Check if to date is empty
      if (!dateRangeField.dateRangeValueToString || dateRangeField.dateRangeValueToString.trim() === '') {
        return false;
      }

      // Validate that from date is before to date
      const fromDate = new Date(dateRangeField.dateRangeValueFromString);
      const toDate = new Date(dateRangeField.dateRangeValueToString);

      // Check if dates are valid
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        return false;
      }

      // Check if from date is smaller than to date
      if (fromDate >= toDate) {
        return false;
      }

      // Check min/max date constraints if they exist
      if (dateRangeField.minDate) {
        const minDate = new Date(dateRangeField.minDate);
        if (!isNaN(minDate.getTime()) && fromDate < minDate) {
          return false;
        }
      }

      if (dateRangeField.maxDate) {
        const maxDate = new Date(dateRangeField.maxDate);
        if (!isNaN(maxDate.getTime()) && toDate > maxDate) {
          return false;
        }
      }

      // Check if field has inputPattern validation
      if (dateRangeField.fieldMeta.inputPattern) {
        const pattern = new RegExp(dateRangeField.fieldMeta.inputPattern);
        if (!pattern.test(dateRangeField.dateRangeValueFromString) || 
            !pattern.test(dateRangeField.dateRangeValueToString)) {
          return false;
        }
      }
    }

    return true;
  }

  get isFormValid(): boolean {
    const isBaseFormValid = this.baseEntityFormComponent?.isBaseFormValid || false;
    if(!isBaseFormValid){
      return false;
    }
    const isObjectiveSpecficFieldsValid = this.isDurationFieldValid || false;
    if(!isObjectiveSpecficFieldsValid){
      return false;
    }
    if(this.objectiveBizEntity?.type?.additionalFields?.length > 0){
      const isAdditionalInfoValid = this.additionalInfoFormComponent?.isAdditionalInfoValid || false;
      if (!isAdditionalInfoValid) {
        return false;
      }
    }
    if (this.relationshipFieldsComponents?.length > 0) {
      const allRelationshipsValid = this.relationshipFieldsComponents.toArray().every(rf => rf.isFormValid());
      if (!allRelationshipsValid) {
        return false;
      }
    }
    return true;
  }

  private populateObjectiveFormWithValues(): IObjective {
    this.mergeBaseBizEntityValues();
    this.mergeDateRange();
    this.mergeAdditionalInfo();
    return this.objectiveBizEntity;
  }
  private extractTargetBizEntities(relationshipData: IBizEntityRelationships[]): IBizEntity[] {
    const targetBizEntities: IBizEntity[] = [];
    relationshipData.forEach((relationship: IBizEntityRelationships) => {
      if (relationship.to && Array.isArray(relationship.to)) {
        relationship.to.forEach((to: IBizEntityRelationTarget) => {
          if (to.target.bizEntity) {
            targetBizEntities.push(to.target.bizEntity as IBizEntity);
          }
        });
      }
    });
    return targetBizEntities;
  }

  onSave(): void {

    if (!this.isFormValid) {
      this.toastService.toast('Please fill all the required fields.');
      return;
    }

    if (this.isAPICallInProgress) {
      this.toastService.toast('Please wait, operation in progress.');
      return;
    }

    this.setAPICallInProgress(true);
    this.populateObjectiveFormWithValues();
    if (this.objectiveBizEntity && this.isFormValid) {
      if (this.isEditing) {
        this.objectiveStateStoreService.update(this.objectiveBizEntity)?.then(async (response) => {
          this.appStateService.incomingBizEntitySelectionId = response['bizEntityId'] ? String(response['bizEntityId']) : null;
          this.baseEntityFormComponent.afterSubmissionSucceeded();
          if (this.doesBizEntityHaveRelationship()) {
            await this.handleEditRelationships();
          }
          this.setAPICallInProgress(false);
        }).catch((error) => {
          this.toastService.toast('Error occured while updating entity');
          this.setAPICallInProgress(false);
        });
      } else {
        if (this.doesBizEntityHaveRelationship()) {
          this.retrieveAdditionalEntityDetails();
        }
        else {
          this.objectiveStateStoreService.create(this.objectiveBizEntity)?.then((response) => {
            notifyStrategyEntityCreated(this.objectiveBizEntity, response, () => {
              this.appStateService.incomingBizEntitySelectionId = response['bizEntityId'] ? String(response['bizEntityId']) : null;
            });
            this.baseEntityFormComponent.afterSubmissionSucceeded();
            this.setAPICallInProgress(false);
          }).catch((error) => {
            this.toastService.toast('Error occured while creating entity');
            this.setAPICallInProgress(false);
          });
        }
      }
    } else {
      this.setAPICallInProgress(false);
    }
  }
  private doesBizEntityHaveRelationship(): boolean {
    if (this.objectiveBizEntity?.type && this.objectiveBizEntity.type.relationshipConfiguration && this.objectiveBizEntity.type.relationshipConfiguration.length > 0) {
      this.relationshipConfigurations = this.objectiveBizEntity.type.relationshipConfiguration;
      return true;
    }
    return false;
  }
  onRelationshipFieldsChange(data: any): void {
  }
  private buildFormFields(): IPolymorphicControlMeta[] {
    const fields: IPolymorphicControlMeta[] = [];
    let duration: IPolymorphicControlMeta = this.polymorphicControlService.getC({
      id: 'duration',
      type: PolyMorphicControlTypes.rangeDate,
      label: 'Duration',
      icon: 'cilCalendar',
      value: this.objectiveBizEntity?.duration?.from && this.objectiveBizEntity?.duration?.to 
        ? {
            from: this.utilService.convertDateToString(this.objectiveBizEntity.duration.from),
            to: this.utilService.convertDateToString(this.objectiveBizEntity.duration.to),
          }
        : { from: '', to: '' },
      dateRangeValueFrom: this.objectiveBizEntity?.duration?.from,
      dateRangeValueTo: this.objectiveBizEntity?.duration?.to,
      dateRangeValueFromString: this.utilService.convertDateToString(this.objectiveBizEntity?.duration?.from),
      dateRangeValueToString: this.utilService.convertDateToString(this.objectiveBizEntity?.duration?.to),
      fieldMeta: {
        id: 'duration',
        name: 'Duration',
        type: FieldTypes.rangeDate,
        label: 'Duration Field',
        isRequired: true,
      },
    });
    
    /**
     * ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️
     * These are business rules.Should be loaded by
     * code at runtime and handled as data, not as logic.
     * ⚠️⚠️⚠️ BUSINESS RULES NOTICE ⚠️⚠️⚠️
     * */
  // Apply parent duration constraints only if they exist
    if (this.parentDurationConstraints) {
      duration = this.durationConstraintsService.applyDurationConstraints(duration, this.parentDurationConstraints, 'Objective');
    }
    
    fields.push(duration);
    return fields;
  }
  private mergeAdditionalInfo(): void {
    this.objectiveBizEntity.additionalInfo = this.additionalInfoFormComponent?.additionalInfoFormValues || [];
  }
  private mergeBaseBizEntityValues(): void {
    this.objectiveBizEntity.name = this.baseEntityFormComponent.bizEntityFormValues?.name || '';
    this.objectiveBizEntity.description = this.baseEntityFormComponent.bizEntityFormValues?.description || '';
    this.objectiveBizEntity.icon = this.baseEntityFormComponent.bizEntityFormValues?.icon || '';
    this.objectiveBizEntity.parentId = this.baseEntityFormComponent.bizEntityFormValues?.parentId || '';
    this.objectiveBizEntity.ownerId = this.baseEntityFormComponent.bizEntityFormValues?.ownerId || '';
  }

  onFormReady(): void {
    this.baseFormReady = true;
    this.tryHideFormLoading();
  }

  /**
   * Event handler called when user selects a different parent entity in the form.
   * Loads parent objective data and rebuilds form with new duration constraints.
   */
  onParentEntitySet(parentEntity: IBizEntity | null): Promise<void> {
    if (!parentEntity) {
      // Parent cleared - reset constraints and rebuild form
      this.parentDurationConstraints = null;
      this.buildFormLayout();
      return Promise.resolve();
    }

    // Load parent objective data asynchronously
    // Reset flow data flag and show loading state
    this.flowDataReady = false;
    this.isFormLoading = true;

    return this.bizEntityParentHierarchyService.resolveAndFetchObjectiveFromParentHierarchy(parentEntity)
      .then((objective) => {
        this.parentDurationConstraints = objective?.duration 
          ? this.bizEntityParentHierarchyService.getDurationDisplayValue(objective)
          : null;
        
        // Rebuild form with new constraints
        this.buildFormLayout();
      })
      .catch(() => {
        // Handle error silently - no constraints
        this.parentDurationConstraints = null;
        this.buildFormLayout();
      })
      .finally(() => {
        this.markFlowDataReady();
      });
  }

  private mergeDateRange(): void {
    let dateRangeField = this.formLayout.content?.fieldItems?.find(field => field.id === 'duration');
    this.objectiveBizEntity.duration = new DateRange(
      new Date(dateRangeField?.dateRangeValueFromString || ''),
      new Date(dateRangeField?.dateRangeValueToString || '')
    );
  }
  getTargetBizEntity(config: IRelationshipConfiguration): IBizEntity[] {
    if(!this.extractedTargetBizEntities){
      return [];
    }
    if (config?.targetType === RelationshipTargetType.BizEntityType) {
      return this.extractedTargetBizEntities.filter(
        bizEntity => bizEntity.type?.id.toString() === (config.target as IBizEntityType).id.toString() ||
          bizEntity.type?.basedOnTypeId?.toString() === (config.target as IBizEntityType).id?.toString()
      );
    } else {
      return this.extractedTargetBizEntities.filter(
        bizEntity => bizEntity.type?.category === (config.target as BizEntityTypeCategory)
      );
    }
  }
  onBizEntityAdditionalInfo(data: { config: IRelationshipConfiguration, name: string, additionalInfo: IAdditionalInfo[] }[], index: number): void {
    // Since the component now emits an array of blocks/selections for each relationship config,
    // we need to flatten and store all entries. Remove old entries for this config first.
    this.relationshipAdditionalInfo = this.relationshipAdditionalInfo.filter(
      item => item.config !== data[0]?.config
    );
    // Add all new entries
    this.relationshipAdditionalInfo.push(...data);
  }
  onBizEntityAdditionalInfoForEdit(data: { config: IRelationshipConfiguration, bizEntity: IBizEntity }) {
    this.relationshipAdditionalInfoForEdit.push(data);
  }

  private async handleEditRelationships(): Promise<void> {
    this.relationshipFieldsComponents?.forEach(rf => rf.getRelationshipFormFieldValues());
    if (this.relationshipAdditionalInfo.length === 0) return;

    const registeredRelationshipRecords: IBizEntityRelationTarget[] = [];
    for (const data of this.relationshipAdditionalInfo) {
      if (data.config.targetType !== RelationshipTargetType.BizEntityType) continue;
      if (!this.objectiveBizEntity) continue;

      if (data.config.link === RelationshipLinkType.Tight) {
        const entityParentId = await this.getEntityParentIdForTightlyLinkedEntity(data.config, this.objectiveBizEntity);
        const tightlyLinkedBizEntity = this.autoCreateEntitiesService.createBizEntityAutomaticallyFromBizEntity(this.objectiveBizEntity, data.name, data.additionalInfo, data.config, entityParentId);
        const tightlyLinkedCategory = tightlyLinkedBizEntity.type.category;
        (tightlyLinkedBizEntity as IBenBizEntity & { appId?: string; tenantId?: string }).appId = (tightlyLinkedBizEntity as IObjective).type?.appId;
        (tightlyLinkedBizEntity as IBenBizEntity & { appId?: string; tenantId?: string }).tenantId = (tightlyLinkedBizEntity as IObjective).type?.tenantId;
        const transformedTightlyLinked = benBizEntitySyncBeforeSendingToAPI(tightlyLinkedBizEntity as IBenBizEntity, this.userContextService);
        registeredRelationshipRecords.push({
          relationshipType: data.config.relationshipType,
          target: { isNew: true, bizEntity: transformedTightlyLinked, category: tightlyLinkedCategory },
        });
      } else {
        data.additionalInfo.forEach((addInfo: IAdditionalInfo & { selectedBizEntity?: IBizEntity }) => {
          if (addInfo.fieldMeta.type === FieldTypes.bizEntity && addInfo.value) {
            const bizEntity = addInfo.selectedBizEntity || this.bizEntities.find(be => be.id.toString() === addInfo.value.toString());
            if (bizEntity) {
              registeredRelationshipRecords.push({
                relationshipType: data.config.relationshipType,
                target: { isNew: false, bizEntity: bizEntity.id, category: bizEntity?.type?.category },
              });
            }
          }
        });
      }
    }

    if (registeredRelationshipRecords.length === 0) return;

    const relations: IBizEntityRelationships = {
      from: { isNew: false, bizEntity: this.objectiveBizEntity.id, category: this.objectiveBizEntity.type.category },
      to: registeredRelationshipRecords,
    };
    await this.relationshipStateStoreService.create(relations, false)?.catch((error) => {
      this.toastService.toast(`Error updating relationships: ${error}`);
    });
  }

  async retrieveAdditionalEntityDetails() {
    if (!this.doesBizEntityHaveRelationship()) {
      this.setAPICallInProgress(false);
      return;
    }
    if (this.isRelationshipAdditionalInfoValid()) {
      // if(this.isFormValid){
      if (!this.isEditing) {
        let registeredRelationshipRecords: IBizEntityRelationTarget[] = [];
        for (const data of this.relationshipAdditionalInfo) {
          let targetType = data.config.targetType;
          if (targetType === RelationshipTargetType.BizEntityType) {
            if (!this.objectiveBizEntity) { continue; }
            if (data.config.link === RelationshipLinkType.Tight) {
              const entityParentId = await this.getEntityParentIdForTightlyLinkedEntity(data.config, this.objectiveBizEntity);
              let tightlyLinkedBizEntity = this.autoCreateEntitiesService.createBizEntityAutomaticallyFromBizEntity(this.objectiveBizEntity, data.name, data.additionalInfo, data.config, entityParentId);
              const tightlyLinkedCategory = tightlyLinkedBizEntity.type.category;
              (tightlyLinkedBizEntity as IBenBizEntity & { appId?: string; tenantId?: string }).appId = (tightlyLinkedBizEntity as IObjective).type?.appId;
              (tightlyLinkedBizEntity as IBenBizEntity & { appId?: string; tenantId?: string }).tenantId = (tightlyLinkedBizEntity as IObjective).type?.tenantId;
              const transformedTightlyLinked = benBizEntitySyncBeforeSendingToAPI(tightlyLinkedBizEntity as IBenBizEntity, this.userContextService);
              registeredRelationshipRecords.push({
                relationshipType: data.config.relationshipType,
                target: {
                  isNew: true,
                  bizEntity: transformedTightlyLinked,
                  category: tightlyLinkedCategory
                },
              });
            } else {
              // let bizEntity = this.benBizEntites.find(bizEntity => bizEntity.id.toString() === data.additionalInfo[0].value.toString());
              data.additionalInfo.forEach((addInfo: IAdditionalInfo & { selectedBizEntity?: IBizEntity }) => {
                if (addInfo.fieldMeta.type === FieldTypes.bizEntity && addInfo.value) {
                  let bizEntity = addInfo.selectedBizEntity || this.bizEntities.find(bizEntity => bizEntity.id.toString() === addInfo.value.toString());
                  if (bizEntity) {
                    registeredRelationshipRecords.push({
                      relationshipType: data.config.relationshipType,
                      target: {
                        isNew: false,
                        bizEntity: bizEntity.id,
                        category: bizEntity?.type.category
                      },
                    });
                  }
                }
              });
              // if(bizEntity){
              //   registeredRelationshipRecords.push({
              //     relationshipType: data.config.relationshipType,
              //     target:{
              //       isNew:false,
              //       bizEntity: bizEntity.id,
              //       category: bizEntity?.type.category
              //     },
              //   });
              // }
            }
          }
        }
        (this.objectiveBizEntity as IObjective & { appId?: string; tenantId?: string }).appId = (this.objectiveBizEntity as IObjective).type?.appId;
        (this.objectiveBizEntity as IObjective & { appId?: string; tenantId?: string }).tenantId = (this.objectiveBizEntity as IObjective).type?.tenantId;
        const transformedBizEntity = objectivesSyncBeforeSendingToAPI(this.objectiveBizEntity as IObjective, this.userContextService);
        let relations: IBizEntityRelationships = {
          from: {
            isNew: true,
            bizEntity: transformedBizEntity,
            category: (this.objectiveBizEntity!).type.category
          },
          to: registeredRelationshipRecords,
        }
        this.relationshipStateStoreService.create(relations, false)?.then((response) => {
          notifyStrategyEntityCreated(this.objectiveBizEntity, response, () => {
            this.appStateService.incomingBizEntitySelectionId = response['bizEntityId'] ? String(response['bizEntityId']) : null;
          });
          this.baseEntityFormComponent.afterSubmissionSucceeded();
          this.setAPICallInProgress(false);
        }).catch((error) => {
          this.toastService.toast(`Error creating relationships: ${error}`);
          this.setAPICallInProgress(false);
        });
      }
      else {
        this.setAPICallInProgress(false);
        // let registeredRelationshipRecords: IBizEntityRelationTarget[] = [];
        // this.relationshipAdditionalInfoForEdit.forEach((data: {config: IRelationshipConfiguration, bizEntity: IBizEntity}) => {
        //   registeredRelationshipRecords.push({
        //     relationshipType: data.config.relationshipType,
        //     target: {
        //       isNew: false,
        //       bizEntity: data.bizEntity?.id || '',
        //       category: data.bizEntity?.type?.category!
        //     },
        //   });
        // });
        // let relations: IBizEntityRelationships = {
        //   from:{
        //     isNew: false,
        //     bizEntity: this.bizEntity?.id || '',
        //     category: this.bizEntity?.type?.category!
        //   },
        //   to: registeredRelationshipRecords,
        // }
        // console.log("this.relationshipAdditionalInfoForEdit", relations);
        // // this.relationshipStateStoreService.update(relations, this.userService);
      }
      // }
    }
    else {
      this.toastService.toast('Please provide all the fields of the related entity Biz Entity.');
      this.setAPICallInProgress(false);
    }
  }

  private setAPICallInProgress(inProgress: boolean): void {
    this.isAPICallInProgress = inProgress;
    //TO Do : This diable/enable the submit button in the footer should be handled using key instead of index 0
   if (this.formFooterLayout && this.formFooterLayout.footer && this.formFooterLayout.footer.actions.length > 0) {
    this.formFooterLayout.footer.actions[0].isDisabled = inProgress;
  }
  }
  isRelationshipAdditionalInfoValid(): boolean {
    // Trigger all relationship field components to emit their data
    this.relationshipFieldsComponents.forEach(relationshipField => {
      relationshipField.getRelationshipFormFieldValues();
    });
    let isValid = true;
    this.relationshipAdditionalInfo.forEach((data: { config: IRelationshipConfiguration, name: string, additionalInfo: IAdditionalInfo[] }) => {
      data.additionalInfo.forEach((addInfo: IAdditionalInfo) => {
        if (addInfo.fieldMeta.isRequired && !addInfo.value) {
          isValid = false;
          return;
        }
      });
    });
    return isValid;
  }

  async getEntityParentIdForTightlyLinkedEntity(config: IRelationshipConfiguration, currentEntity: IBizEntity): Promise<string | undefined> {
    if(config?.targetType === RelationshipTargetType.BizEntityType){
      const targetBizEntityTypeAppId = (config.target as IBizEntityType).appId;
      if(targetBizEntityTypeAppId && this.currentAppId !== targetBizEntityTypeAppId){
        // const tenants = await this.tenantStateStoreService.fetch();
        // const tenant = tenants?.[0];
        // return tenant?.id;
        const xappCap = {
          appId: targetBizEntityTypeAppId,
        }

        const appRootEntity = await this.appRootStateStoreService.get(undefined, undefined, xappCap);
        return appRootEntity?.id?.toString();
      }
      return currentEntity.parentId?.toString();
    }
    return undefined;
  }

  private markFlowDataReady(): void {
    this.flowDataReady = true;
    this.tryHideFormLoading();
  }

  private tryHideFormLoading(): void {
    if (this.baseFormReady && this.flowDataReady) {
      this.isFormLoading = false;
    }
  }
}
